import { useState } from "react";
import { KeyRound, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordForm = ({ onCodeSent, onBack }) => {
  const [resetEmail, setResetEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!resetEmail.trim()) { setError("Please enter your email"); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email: resetEmail });
      if (data.email_sent) {
        toast.success("Reset code sent to your email!");
        onCodeSent({ email: resetEmail, emailSent: true, displayCode: null });
      } else if (data.code) {
        toast.info("Email delivery failed — code shown on screen");
        onCodeSent({ email: resetEmail, emailSent: false, displayCode: data.code });
      } else {
        onCodeSent({ email: resetEmail, emailSent: false, displayCode: null });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button onClick={onBack} className="flex items-center gap-1 mb-4 text-sm" style={{ color: "var(--color-text-dim)" }}>
        <ArrowLeft className="w-4 h-4" /> Back to Login
      </button>

      <div className="flex items-center gap-3 mb-4">
        <KeyRound className="w-8 h-8" style={{ color: "var(--color-accent)" }} />
        <h2 className="text-2xl font-black" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
          Reset Password
        </h2>
      </div>

      <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
        Enter the email address you used to register. We'll send a reset code to your inbox.
      </p>

      {error && <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="forgot-error">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-muted)" }}>Email Address</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: "var(--color-text-dim)" }} />
            <Input type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
              placeholder="you@example.com" className="pl-10 rounded-lg"
              style={{ backgroundColor: "var(--color-input-bg)", borderColor: "var(--color-border)", color: "var(--color-text)" }}
              data-testid="reset-email-input" required />
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full rounded-lg py-3 font-bold text-white"
          style={{ backgroundColor: "var(--color-accent)" }} data-testid="send-reset-code-button">
          {loading ? "Sending..." : "Send Reset Code"}
        </Button>
      </form>
    </>
  );
};

export default ForgotPasswordForm;
